import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useMatch, useNavigate, useParams, useLocation } from 'react-router-dom';
import henceforthApi from '../utils/henceforthApi';
import { GlobalContext } from "../context/Provider";
import PaginationLayout from "../components/PaginationLayout";
import moment from 'moment';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { ColDef } from 'ag-grid-community';
import { Edit2, Eye, MoreHorizontal, Plus, Search, Trash2 } from 'lucide-react';

import AgGridTable from '../components/ui/AgGridTable';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

interface modalData {
  name: string;
}
interface modalFecth {
  brand_id: null | string;
  brand_name: string;
  created_at: string;
  name: string;
  _id: string;
}
interface dateFetch {
  total_count: number;
  data: Array<modalFecth>;
}
interface modalG {
  created_at: string;
  modal: dateFetch;
  name: string;
  _id: string;
}
interface modalObje {
  total_count: number;
  data: Array<modalData>;
}
interface Arrays {
  name: string;
  _id: string;
  modal: modalObje;
}
interface state {
  brand: Array<Arrays>;
  total_count: null | number;
  loading: boolean;
}

const Vehicle = () => {
  const [activeTab, setActiveTab] = useState<'brands' | 'types' | 'colors'>('brands');

  let navigate = useNavigate();
  let location = useLocation();
  const { authState, handleFilter, handlePagination, handleError } = useContext(GlobalContext);
  const params: any = useParams();
  const newParam = new URLSearchParams(location.search);
  const [searchData, setSearchData] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({
    brand: [],
    total_count: null,
    loading: false
  } as state);

  // --- Vehicle Types State ---
  const [typesList, setTypesList] = useState<Array<any>>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesTotalCount, setTypesTotalCount] = useState(0);
  const [typesPage, setTypesPage] = useState(1);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<any>(null);
  const [typeNameInput, setTypeNameInput] = useState('');
  const [savingType, setSavingType] = useState(false);

  // --- Vehicle Colors State ---
  const [colorsList, setColorsList] = useState<Array<any>>([]);
  const [colorsLoading, setColorsLoading] = useState(false);
  const [colorsTotalCount, setColorsTotalCount] = useState(0);
  const [colorsPage, setColorsPage] = useState(1);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [editingColor, setEditingColor] = useState<any>(null);
  const [colorNameInput, setColorNameInput] = useState('');
  const [savingColor, setSavingColor] = useState(false);

  if (authState.access_token) { henceforthApi.setToken(authState.access_token); }
  const limit = 10;

  // --- Get Brands ---
  const GetVehicle = async () => {
    let search = newParam.has("search") ? newParam.get("search") : "";
    try {
      setLoading(true);
      let apiRes = await henceforthApi.Vehicle.getBrand(String(search), Number(params.page) - 1, limit);
      let brandData = apiRes.data;
      let allBrandData = brandData.data;
      let total_count = brandData.total_count;
      if (Array.isArray(allBrandData)) {
        let newBrand = [] as Array<modalG>;
        await Promise.all(allBrandData.map(async (res) => {
          try {
            const modalsData = await henceforthApi.Vehicle.getModals(res.name, 0, '');
            newBrand.push({
              ...res,
              modal: modalsData.data,
            });
          } catch (error: any) { }
        }));

        setState({
          ...state,
          brand: newBrand,
          total_count,
          loading: false
        });
      }
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // --- Get Vehicle Types ---
  const getVehicleTypes = async (pageNumber: number = 1) => {
    try {
      setTypesLoading(true);
      const res = await henceforthApi.Vehicle.getTypes(pageNumber - 1, limit);
      const data = res?.data?.data || [];
      const total = res?.data?.total_count || 0;
      setTypesList(data);
      setTypesTotalCount(total);
      setTypesPage(pageNumber);
    } catch (err: any) {
      setTypesList([]);
      setTypesTotalCount(0);
      handleError(err);
    } finally {
      setTypesLoading(false);
    }
  };

  // --- Save / Edit Vehicle Type ---
  const handleSaveType = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = typeNameInput.trim();
    if (!trimmed) {
      toast.warn("Please enter vehicle type name");
      return;
    }
    setSavingType(true);
    try {
      const payload: any = { name: trimmed };
      if (editingType?._id) {
        payload._id = editingType._id;
      }
      await henceforthApi.Vehicle.addType(payload);
      Swal.fire({
        title: editingType?._id ? "Type Updated" : "Type Added",
        text: editingType?._id ? "Vehicle type updated successfully" : "Vehicle type added successfully",
        icon: "success",
        confirmButtonText: "OK",
      });
      setTypeModalOpen(false);
      setEditingType(null);
      setTypeNameInput('');
      getVehicleTypes(typesPage);
    } catch (err: any) {
      const errorMsg = err?.response?.body?.error_description || err?.response?.body?.message || (typeof err === 'string' ? err : "Failed to save vehicle type");
      Swal.fire({
        title: "Failed",
        text: errorMsg,
        icon: "error",
        confirmButtonText: "Cancel",
      });
    } finally {
      setSavingType(false);
    }
  };

  // --- Delete Vehicle Type ---
  const handleDeleteType = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#006747",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          await henceforthApi.Vehicle.delType(id);
          Swal.fire({
            title: "Deleted",
            text: "Vehicle type deleted successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
          getVehicleTypes(typesPage);
        } catch (err: any) {
          Swal.fire({
            title: "Delete Failed",
            text: err?.response?.body?.error_description || "Failed to delete",
            icon: "error",
            confirmButtonText: "Cancel",
          });
        }
      }
    });
  };

  // --- Get Vehicle Colors ---
  const getVehicleColors = async (pageNumber: number = 1) => {
    try {
      setColorsLoading(true);
      const res = await henceforthApi.Vehicle.getColors(pageNumber - 1, limit);
      const data = res?.data?.data || [];
      const total = res?.data?.total_count || 0;
      setColorsList(data);
      setColorsTotalCount(total);
      setColorsPage(pageNumber);
    } catch (err: any) {
      setColorsList([]);
      setColorsTotalCount(0);
      handleError(err);
    } finally {
      setColorsLoading(false);
    }
  };

  // --- Save / Edit Vehicle Color ---
  const handleSaveColor = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = colorNameInput.trim();
    if (!trimmed) {
      toast.warn("Please enter vehicle color name");
      return;
    }
    setSavingColor(true);
    try {
      const payload: any = { name: trimmed };
      if (editingColor?._id) {
        payload._id = editingColor._id;
      }
      await henceforthApi.Vehicle.addColor(payload);
      Swal.fire({
        title: editingColor?._id ? "Color Updated" : "Color Added",
        text: editingColor?._id ? "Vehicle color updated successfully" : "Vehicle color added successfully",
        icon: "success",
        confirmButtonText: "OK",
      });
      setColorModalOpen(false);
      setEditingColor(null);
      setColorNameInput('');
      getVehicleColors(colorsPage);
    } catch (err: any) {
      const errorMsg = err?.response?.body?.error_description || err?.response?.body?.message || (typeof err === 'string' ? err : "Failed to save vehicle color");
      Swal.fire({
        title: "Failed",
        text: errorMsg,
        icon: "error",
        confirmButtonText: "Cancel",
      });
    } finally {
      setSavingColor(false);
    }
  };

  // --- Delete Vehicle Color ---
  const handleDeleteColor = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#006747",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          await henceforthApi.Vehicle.delColor(id);
          Swal.fire({
            title: "Deleted",
            text: "Vehicle color deleted successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
          getVehicleColors(colorsPage);
        } catch (err: any) {
          Swal.fire({
            title: "Delete Failed",
            text: err?.response?.body?.error_description || "Failed to delete",
            icon: "error",
            confirmButtonText: "Cancel",
          });
        }
      }
    });
  };

  useEffect(() => {
    if (activeTab === 'brands') {
      GetVehicle();
    }
  }, [params.page, newParam.get("search"), activeTab]);

  // Brand Column Defs
  const brandColumnDefs = useMemo<ColDef<Arrays>[]>(() => {
    const pageNum = Number(params.page) || 1;
    return [
      // {
      //   headerName: "Sr No.",
      //   field: "_id",
      //   width: 75,
      //   minWidth: 70,
      //   maxWidth: 85,
      //   suppressSizeToFit: true,
      //   valueGetter: (p) => {
      //     if (!p.node || p.node.rowIndex === null || p.node.rowIndex === undefined) return "";
      //     return (pageNum - 1) * limit + (p.node.rowIndex + 1);
      //   },
      // },
      {
        headerName: "Vehicle Brand",
        field: "name",
        minWidth: 200,
        flex: 1.2,
        cellRenderer: (p: any) => {
          return (
            <span className="font-semibold text-slate-800 text-sm">
              {p.value || "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Vehicle Models",
        field: "modal" as any,
        minWidth: 320,
        flex: 2.5,
        cellRenderer: (p: any) => {
          const modals = p.data?.modal?.data;
          if (!modals || modals.length === 0) {
            return <span className="text-slate-400 text-sm">No models registered</span>;
          }
          const display = modals.slice(0, 6);
          const hasMore = modals.length > 6;
          return (
            <div className="flex items-center gap-1.5 flex-wrap py-1">
              {display.map((m: any, idx: number) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-slate-100 text-slate-700 border border-slate-200/60"
                >
                  {m?.name}
                </span>
              ))}
              {hasMore && (
                <span className="text-sm text-slate-500 font-medium">
                  +{modals.length - 6} more
                </span>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        pinned: "right",
        cellRenderer: (p: any) => {
          const row = p.data;
          const brandId = row?._id;

          return (
            <div className="flex items-center justify-center h-full">
              <Button
                type="button"
                onClick={() => navigate(`/vehicle-details/1/${brandId}`)}
                title="View Models"
                variant='ghost'
              >
                <Eye className="w-4 h-4" />
              </Button>
            </div>
          );
        },
      },
    ];
  }, [params.page]);

  // Types Column Defs
  const typesColumnDefs = useMemo<ColDef<any>[]>(() => {
    return [
      {
        headerName: "Sr No.",
        field: "_id",
        width: 75,
        minWidth: 70,
        maxWidth: 85,
        suppressSizeToFit: true,
        valueGetter: (p) => {
          if (!p.node || p.node.rowIndex === null || p.node.rowIndex === undefined) return "";
          return (typesPage - 1) * limit + (p.node.rowIndex + 1);
        },
      },
      {
        headerName: "Vehicle Type",
        field: "name",
        minWidth: 220,
        flex: 1.5,
        cellRenderer: (p: any) => {
          return (
            <span className="font-semibold text-slate-800 text-sm">
              {p.value || "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Created Date",
        field: "created_at",
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => {
          const dt = p.value ? moment(Number(p.value)).format("DD MMM YYYY") : "Not available";
          return <span className="text-slate-600 text-sm">{dt}</span>;
        },
      },
      {
        headerName: "Actions",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        pinned: "right",
        cellRenderer: (p: any) => {
          const item = p.data;
          return (
            <div className="flex items-center justify-center h-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="table-action-dots p-1 text-slate-500 hover:text-slate-800 focus:outline-none"
                    aria-label="Actions"
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36">
                  <DropdownMenuItem
                    onClick={() => {
                      setEditingType(item);
                      setTypeNameInput(item.name);
                      setTypeModalOpen(true);
                    }}
                    className="gap-2 cursor-pointer text-slate-700"
                  >
                    <Edit2 className="w-4 h-4 text-slate-500" />
                    <span>Edit Type</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleDeleteType(item._id)}
                    className="gap-2 cursor-pointer text-rose-600 hover:text-rose-700"
                  >
                    <Trash2 className="w-4 h-4 text-rose-500" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ];
  }, [typesPage]);

  // Colors Column Defs
  const colorsColumnDefs = useMemo<ColDef<any>[]>(() => {
    return [
      {
        headerName: "Sr No.",
        field: "_id",
        width: 75,
        minWidth: 70,
        maxWidth: 85,
        suppressSizeToFit: true,
        valueGetter: (p) => {
          if (!p.node || p.node.rowIndex === null || p.node.rowIndex === undefined) return "";
          return (colorsPage - 1) * limit + (p.node.rowIndex + 1);
        },
      },
      {
        headerName: "Vehicle Color",
        field: "name",
        minWidth: 220,
        flex: 1.5,
        cellRenderer: (p: any) => {
          const colorName = p.value || "Not available";
          return (
            <div className="flex items-center gap-2 py-1">
              <span
                className="w-4 h-4 rounded-full border border-slate-300 shadow-2xs shrink-0"
                style={{ backgroundColor: colorName.toLowerCase().trim() }}
              />
              <span className="font-semibold text-slate-800 text-sm capitalize">
                {colorName}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Created Date",
        field: "created_at",
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => {
          const dt = p.value ? moment(Number(p.value)).format("DD MMM YYYY") : "Not available";
          return <span className="text-slate-600 text-sm">{dt}</span>;
        },
      },
      {
        headerName: "Actions",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        pinned: "right",
        cellRenderer: (p: any) => {
          const item = p.data;
          return (
            <div className="flex items-center justify-center h-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="table-action-dots p-1 text-slate-500 hover:text-slate-800 focus:outline-none"
                    aria-label="Actions"
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36">
                  <DropdownMenuItem
                    onClick={() => {
                      setEditingColor(item);
                      setColorNameInput(item.name);
                      setColorModalOpen(true);
                    }}
                    className="gap-2 cursor-pointer text-slate-700"
                  >
                    <Edit2 className="w-4 h-4 text-slate-500" />
                    <span>Edit Color</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleDeleteColor(item._id)}
                    className="gap-2 cursor-pointer text-rose-600 hover:text-rose-700"
                  >
                    <Trash2 className="w-4 h-4 text-rose-500" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ];
  }, [colorsPage]);

  // Filtered types/colors based on searchData
  const filteredTypes = useMemo(() => {
    if (!searchData) return typesList;
    return typesList.filter((item) =>
      item.name?.toLowerCase().includes(searchData.toLowerCase())
    );
  }, [typesList, searchData]);

  const filteredColors = useMemo(() => {
    if (!searchData) return colorsList;
    return colorsList.filter((item) =>
      item.name?.toLowerCase().includes(searchData.toLowerCase())
    );
  }, [colorsList, searchData]);

  const totalCountDisplay =
    activeTab === 'brands'
      ? `${Number(state.total_count || 0)} brands`
      : activeTab === 'types'
        ? `${typesTotalCount} types`
        : `${colorsTotalCount} colors`;

  return (
    <>
      {/* Compact Single Toolbar Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-60">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder={
              activeTab === 'brands'
                ? "Search brand..."
                : activeTab === 'types'
                  ? "Search type..."
                  : "Search color..."
            }
            value={searchData}
            onChange={(e) => {
              setSearchData(e.target.value);
              if (activeTab === 'brands') {
                handleFilter("search", e.target.value);
              }
            }}
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        {/* Right: Tab Filter Dropdown + Action Button */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Category Tab Switcher */}
          <div className="w-44">
            <Select
              value={activeTab}
              onValueChange={(val: any) => {
                setActiveTab(val);
                setSearchData("");
                if (val === 'types') getVehicleTypes(1);
                if (val === 'colors') getVehicleColors(1);
              }}
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="brands">Brands & Models</SelectItem>
                <SelectItem value="types">Vehicle Types</SelectItem>
                <SelectItem value="colors">Vehicle Colors</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action Buttons */}
          {activeTab === 'brands' && (
            <Link to="/vehicle/add">
              <Button
                variant="default"
                size="sm"
                className="h-9 px-4 text-sm gap-1.5 shadow-xs"
              >
                <Plus className="w-4 h-4" />
                <span>Add Brand & Model</span>
              </Button>
            </Link>
          )}
          {activeTab === 'types' && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setEditingType(null);
                setTypeNameInput('');
                setTypeModalOpen(true);
              }}
              className="h-9 px-4 text-sm gap-1.5 shadow-xs"
            >
              <Plus className="w-4 h-4" />
              <span>Add Type</span>
            </Button>
          )}
          {activeTab === 'colors' && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setEditingColor(null);
                setColorNameInput('');
                setColorModalOpen(true);
              }}
              className="h-9 px-4 text-sm gap-1.5 shadow-xs"
            >
              <Plus className="w-4 h-4" />
              <span>Add Color</span>
            </Button>
          )}
        </div>
      </div>

      {/* ================= TAB 1: BRANDS & MODELS ================= */}
      {activeTab === 'brands' && (
        <>
          <AgGridTable
            rowData={state.brand}
            columnDefs={brandColumnDefs}
            loading={loading}
            domLayout="autoHeight"
            emptyMessage="No vehicle brands found"
          />

          <div className="mt-3">
            <PaginationLayout
              count={Number(state.total_count || 0)}
              data={state.brand}
              page={Number(params.page)}
              limit={limit}
              loading={loading}
              onPageChange={(val) => handlePagination(val)}
            />
          </div>
        </>
      )}

      {/* ================= TAB 2: VEHICLE TYPES ================= */}
      {activeTab === 'types' && (
        <>
          <AgGridTable
            rowData={filteredTypes}
            columnDefs={typesColumnDefs}
            loading={typesLoading}
            domLayout="autoHeight"
            emptyMessage="No vehicle types found"
          />

          <div className="mt-3">
            <PaginationLayout
              count={typesTotalCount}
              data={typesList}
              page={typesPage}
              limit={limit}
              loading={typesLoading}
              onPageChange={(page: number) => getVehicleTypes(page)}
            />
          </div>
        </>
      )}

      {/* ================= TAB 3: VEHICLE COLORS ================= */}
      {activeTab === 'colors' && (
        <>
          <AgGridTable
            rowData={filteredColors}
            columnDefs={colorsColumnDefs}
            loading={colorsLoading}
            domLayout="autoHeight"
            emptyMessage="No vehicle colors found"
          />

          <div className="mt-3">
            <PaginationLayout
              count={colorsTotalCount}
              data={colorsList}
              page={colorsPage}
              limit={limit}
              loading={colorsLoading}
              onPageChange={(page: number) => getVehicleColors(page)}
            />
          </div>
        </>
      )}

      {/* --- Modal: Add / Edit Vehicle Type --- */}
      <div
        className={`modal fade common-modal report-modal ${typeModalOpen ? 'show' : ''}`}
        data-bs-backdrop="static"
        tabIndex={-1}
        role={typeModalOpen ? 'dialog' : ''}
        style={{
          display: typeModalOpen ? 'block' : 'none',
          backgroundColor: 'rgba(0,0,0,0.5)'
        }}
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <form className="modal-content" onSubmit={handleSaveType}>
            <div className="modal-header">
              <h4 className="modal-title font-semibold text-slate-800">
                {editingType ? "Edit Vehicle Type" : "Add Vehicle Type"}
              </h4>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setTypeModalOpen(false);
                  setEditingType(null);
                  setTypeNameInput('');
                }}
              ></button>
            </div>
            <div className="modal-body">
              <div>
                <label className="text-sm font-medium text-slate-700">Vehicle Type Name</label>
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder="Enter vehicle type (e.g. Sedan, SUV, Hatchback)"
                  value={typeNameInput}
                  onChange={(e) => setTypeNameInput(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-white me-2"
                onClick={() => {
                  setTypeModalOpen(false);
                  setEditingType(null);
                  setTypeNameInput('');
                }}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-color" disabled={savingType}>
                {savingType ? "Saving..." : editingType ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* --- Modal: Add / Edit Vehicle Color --- */}
      <div
        className={`modal fade common-modal report-modal ${colorModalOpen ? 'show' : ''}`}
        data-bs-backdrop="static"
        tabIndex={-1}
        role={colorModalOpen ? 'dialog' : ''}
        style={{
          display: colorModalOpen ? 'block' : 'none',
          backgroundColor: 'rgba(0,0,0,0.5)'
        }}
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <form className="modal-content" onSubmit={handleSaveColor}>
            <div className="modal-header">
              <h4 className="modal-title font-semibold text-slate-800">
                {editingColor ? "Edit Vehicle Color" : "Add Vehicle Color"}
              </h4>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setColorModalOpen(false);
                  setEditingColor(null);
                  setColorNameInput('');
                }}
              ></button>
            </div>
            <div className="modal-body">
              <div>
                <label className="text-sm font-medium text-slate-700">Vehicle Color Name</label>
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder="Enter vehicle color (e.g. White, Black, Silver, Red)"
                  value={colorNameInput}
                  onChange={(e) => setColorNameInput(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-white me-2"
                onClick={() => {
                  setColorModalOpen(false);
                  setEditingColor(null);
                  setColorNameInput('');
                }}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-color" disabled={savingColor}>
                {savingColor ? "Saving..." : editingColor ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Vehicle;